import IosShareIcon from "@mui/icons-material/IosShare";
import { Box, Button, Typography } from "@mui/material";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { trackClick } from "@/components/_helpers";
import { ENGAGED_EVENT, getInstallState, promptInstall, subscribeInstall } from "@/lib/installPrompt";
import { safeGetItem, safeSetItem } from "@/utils/safeStorage";

const DISMISSED_KEY = "truevined_install_dismissed_at";
const DISMISS_DAYS = 21;
// A fourth page in one visit also counts as engaged.
const PAGE_VIEWS_BEFORE_ASKING = 4;
const HIDDEN_ROOTS = /^\/(admin|newsletter|redirect|r)(\/|$)/;

const dismissedRecently = () => {
  const at = Number(safeGetItem(DISMISSED_KEY) || 0);
  return at > 0 && Date.now() - at < DISMISS_DAYS * 86400000;
};

// "Get the app" card: waits for a sign that someone is enjoying the site (a
// finished game run, or a few pages in one visit), then offers the browser's
// install dialog, or on iPhone Safari the Share, Add to Home Screen steps.
// Docked like UpdateNotification: bottom centre, above the Fan Hub bar,
// below the modal layer.
export function InstallPrompt() {
  const state = useSyncExternalStore(subscribeInstall, getInstallState, getInstallState);
  const location = useLocation();
  const reducedMotion = useReducedMotion();
  const [engaged, setEngaged] = useState(false);
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState(0);
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => setHost(document.body), []);

  useEffect(() => {
    setViews((count) => count + 1);
  }, [location.pathname]);

  useEffect(() => {
    const onEngaged = () => setEngaged(true);
    window.addEventListener(ENGAGED_EVENT, onEngaged);
    return () => window.removeEventListener(ENGAGED_EVENT, onEngaged);
  }, []);

  const adMode = /[?&](adMode|ad)(=|&|$)/.test(location.search);
  const eligible = (state.canPrompt || state.iosManual) && !state.standalone && !adMode && !HIDDEN_ROOTS.test(location.pathname);
  const ready = engaged || views >= PAGE_VIEWS_BEFORE_ASKING;

  useEffect(() => {
    if (!open && eligible && ready && !dismissedRecently()) {
      setOpen(true);
      trackClick("pwa:install-shown", { label: "Install card shown", section: "pwa", value: state.iosManual ? 1 : 0 });
    }
    if (open && !eligible) setOpen(false);
  }, [eligible, ready, open, state.iosManual]);

  const dismiss = () => {
    safeSetItem(DISMISSED_KEY, String(Date.now()));
    setOpen(false);
    trackClick("pwa:install-dismiss", { label: "Install card dismissed", section: "pwa" });
  };

  const install = async () => {
    trackClick("pwa:install-accept", { label: "Install card accepted", section: "pwa" });
    const outcome = await promptInstall();
    if (outcome === "accepted") trackClick("pwa:installed", { label: "App installed", section: "pwa" });
    setOpen(false);
    if (outcome === "dismissed") safeSetItem(DISMISSED_KEY, String(Date.now()));
  };

  if (!host) return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <Box
          component={motion.aside}
          initial={{ opacity: 0, y: reducedMotion ? 0 : 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reducedMotion ? 0 : 10 }}
          transition={{ duration: reducedMotion ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] }}
          aria-label="Get the Truevined app"
          className="InstallPrompt"
          sx={{
            position: "fixed",
            zIndex: 10010,
            left: "0.5rem",
            right: "0.5rem",
            marginInline: "auto",
            bottom: {
              xs: "calc(62px + 0.5rem + 10px + env(safe-area-inset-bottom, 0px))",
              md: "calc(4.1rem + 1.25rem + 0.75rem + env(safe-area-inset-bottom, 0px))",
            },
            maxWidth: { xs: "none", md: "27rem" },
            display: "flex",
            alignItems: "center",
            gap: "14px",
            flexWrap: "wrap",
            padding: "14px 16px",
            borderRadius: "14px",
            border: "1px solid color-mix(in srgb, var(--logoColor, #d9c08a) 24%, transparent)",
            backgroundColor: "var(--color5, #141414)",
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
          }}
        >
          <Box component="img" src="/icons/maskable-192.png" alt="" sx={{ width: 44, height: 44, borderRadius: "12px", flexShrink: 0 }} />
          <Box sx={{ flex: "1 1 180px", minWidth: 0 }}>
            <Typography component="p" sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, color: "var(--logoColor, #d9c08a)", fontSize: { xs: "16px", md: "15px" }, lineHeight: 1.3, m: 0 }}>
              Get the Truevined app
            </Typography>
            <Typography component="p" sx={{ fontFamily: "Poppins, sans-serif", color: "var(--textColor, #e8e8e8)", fontSize: { xs: "14px", md: "13px" }, lineHeight: 1.5, m: "3px 0 0" }}>
              {state.iosManual ? (
                <>
                  Tap <IosShareIcon aria-label="Share" sx={{ fontSize: 16, verticalAlign: "-3px" }} /> Share, then Add to Home Screen. Full screen games, one tap away.
                </>
              ) : (
                "Full screen games, new music first, one tap from your home screen."
              )}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", flexShrink: 0, width: { xs: "100%", md: "auto" } }}>
            {state.canPrompt ? (
              <Button
                type="button"
                onClick={() => void install()}
                sx={{ fontFamily: "Poppins, sans-serif", fontSize: { xs: "15px", md: "14px" }, fontWeight: 600, textTransform: "none", borderRadius: "999px", padding: "8px 20px", minHeight: "40px", color: "var(--backgroundColor, #0b0b0b)", backgroundColor: "var(--logoColor, #d9c08a)", "&:hover": { backgroundColor: "var(--logoColor2, #f0dfb6)" } }}
              >
                Install
              </Button>
            ) : null}
            <Button
              type="button"
              onClick={dismiss}
              sx={{ fontFamily: "Poppins, sans-serif", fontSize: { xs: "15px", md: "14px" }, fontWeight: 500, textTransform: "none", borderRadius: "999px", padding: "8px 14px", minHeight: "40px", color: "var(--textColor, #e8e8e8)" }}
            >
              {state.canPrompt ? "Not now" : "Got it"}
            </Button>
          </Box>
        </Box>
      ) : null}
    </AnimatePresence>,
    host,
  );
}

export default InstallPrompt;
